import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Logo from "../components/Logo";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import Field from "../components/Field";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41 35.4 44 30.1 44 24c0-1.3-.1-2.6-.4-3.9z" />
    </svg>
  );
}

export default function AuthPage() {
  const { t } = useLanguage();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [values, setValues] = useState({ full_name: "", email: "", phone: "", password: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setValues({ ...values, [k]: e.target.value });

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/account';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const validate = () => {
    const e = {};
    if (mode === "register" && (!values.full_name || values.full_name.trim().length < 2)) e.full_name = t.auth.errors.nameRequired;
    if (!values.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) e.email = t.errors.email;
    if (mode === "register" && values.password.length < 8) e.password = t.auth.errors.weakPassword;
    if (mode === "login" && !values.password) e.password = t.errors.required;
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setBusy(true);
    try {
      const payload =
        mode === "register"
          ? { full_name: values.full_name, email: values.email, phone: values.phone, password: values.password }
          : { email: values.email, password: values.password };
      const { data } = await axios.post(`${API}/auth/${mode === "register" ? "register" : "login"}`, payload, {
        withCredentials: true
      });
      setUser(data);
      toast.success(t.auth.welcomeBack);
      navigate("/account");
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) toast.error(t.auth.errors.invalidCreds);
      else if (status === 409) toast.error(t.auth.errors.emailUsed);
      else if (status === 422) toast.error(mode === "register" ? t.auth.errors.weakPassword : t.auth.errors.invalidCreds);
      else toast.error(t.errors.generic);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="auth-page" className="pt-[72px] min-h-screen flex items-center justify-center px-5 py-16">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0F1015] p-8 lg:p-10">
        <div className="flex justify-center"><Logo /></div>

        <div className="mt-8 flex rounded-full border border-white/10 bg-white/[0.03] p-1">
          <button
            type="button"
            data-testid="auth-mode-login"
            onClick={() => { setMode("login"); setErrors({}); }}
            className={`flex-1 rounded-full py-2.5 font-mono2 text-[11px] uppercase transition-all duration-300 ${
              mode === "login" ? "bg-gold text-black font-semibold" : "text-slate-300 hover:text-white"
            }`}
          >
            {t.auth.signIn}
          </button>
          <button
            type="button"
            data-testid="auth-mode-register"
            onClick={() => { setMode("register"); setErrors({}); }}
            className={`flex-1 rounded-full py-2.5 font-mono2 text-[11px] uppercase transition-all duration-300 ${
              mode === "register" ? "bg-gold text-black font-semibold" : "text-slate-300 hover:text-white"
            }`}
          >
            {t.auth.createAccount}
          </button>
        </div>

        <h1 className="mt-7 text-center font-display text-xl font-bold uppercase">
          {mode === "login" ? t.auth.welcomeBack : t.auth.joinUs}
        </h1>
        <p className="mt-2 text-center text-sm text-slate-400">
          {mode === "login" ? t.auth.loginSub : t.auth.registerSub}
        </p>

        <button
          type="button"
          data-testid="google-signin-button"
          onClick={googleLogin}
          className="mt-7 flex w-full items-center justify-center gap-3 rounded-xl border border-white/15 bg-white/[0.05] py-3.5 text-sm font-medium text-white transition-all duration-300 hover:border-gold/50 hover:bg-white/[0.08]"
        >
          <GoogleMark />
          {t.auth.googleBtn}
        </button>

        <div className="mt-6 flex items-center gap-4">
          <span className="h-px flex-1 bg-white/10" />
          <span className="font-mono2 text-[10px] uppercase text-slate-500">{t.auth.or}</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <form className="mt-6 space-y-5" data-testid="auth-form" onSubmit={onSubmit} noValidate>
          {mode === "register" && (
            <>
              <Field id="auth-name" label={t.auth.fullName} error={errors.full_name} required>
                <input
                  id="auth-name"
                  data-testid="auth-name-input"
                  className={`field-input ${errors.full_name ? "has-error" : ""}`}
                  value={values.full_name}
                  onChange={set("full_name")}
                  autoComplete="name"
                />
              </Field>
              <Field id="auth-phone" label={t.auth.phone}>
                <input
                  id="auth-phone"
                  data-testid="auth-phone-input"
                  type="tel"
                  className="field-input"
                  value={values.phone}
                  onChange={set("phone")}
                  autoComplete="tel"
                />
              </Field>
            </>
          )}
          <Field id="auth-email" label={t.auth.email} error={errors.email} required>
            <input
              id="auth-email"
              data-testid="auth-email-input"
              type="email"
              className={`field-input ${errors.email ? "has-error" : ""}`}
              value={values.email}
              onChange={set("email")}
              autoComplete="email"
            />
          </Field>
          <Field id="auth-password" label={t.auth.password} error={errors.password} required>
            <input
              id="auth-password"
              data-testid="auth-password-input"
              type="password"
              className={`field-input ${errors.password ? "has-error" : ""}`}
              value={values.password}
              onChange={set("password")}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              placeholder={mode === "register" ? t.auth.passwordHint : ""}
            />
          </Field>
          {mode === "login" && (
            <button
              type="button"
              data-testid="forgot-password-link"
              onClick={() => navigate("/forgot-password")}
              className="w-full text-right font-mono2 text-[11px] uppercase text-slate-500 hover:text-gold-soft transition-colors"
            >
              {t.auth.forgotLink}
            </button>
          )}
          <button
            type="submit"
            data-testid="auth-submit-button"
            disabled={busy}
            className="btn-gold w-full text-xs font-mono2 uppercase disabled:opacity-60"
          >
            {busy ? "…" : mode === "login" ? t.auth.loginBtn : t.auth.registerBtn}
          </button>
        </form>

        <button
          type="button"
          data-testid="auth-toggle-link"
          onClick={() => { setMode(mode === "login" ? "register" : "login"); setErrors({}); }}
          className="mt-6 w-full text-center font-mono2 text-[11px] uppercase text-slate-400 hover:text-gold-soft transition-colors"
        >
          {mode === "login" ? t.auth.noAccount : t.auth.haveAccount}
        </button>
      </div>
    </main>
  );
}
